import api from 'services/api';
import { array, nullable, num, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// Source of truth for the wire shape: nap-backend `routes/users.js` GET /users, which responds
// `{ users, pagination, config, filtersConfig }`. Only `users` is consumed here, and each user row
// carries more than an LOV needs (mail, public_key, user_settings, …). The projection narrows to the
// identifiers an LOV row renders.

export interface UserSummary {
  readonly user_id: number;
  readonly username: string | null;
}

const summarySchema = object<UserSummary>({
  user_id: num(),
  username: nullable(str()),
});

interface UsersResponse {
  readonly users: readonly UserSummary[];
}

const responseSchema = object<UsersResponse>({
  users: array(summarySchema),
});

const LOV_LIMIT = 1000;

/**
 * Fetch all users the current user can see, for a picker. No pagination — the LOV needs the complete
 * set so Typeahead's client-side filter has every option to match against. If the universe of users
 * ever grows past a UX-bearable dropdown size, this is the call site to migrate to server-side search.
 */
export const getUsersLOV = async (): Promise<readonly UserSummary[]> => {
  const res = await api.get('/users', {
    params: { limit: LOV_LIMIT },
  });
  return refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'getUsersLOV' })).users;
};
